/**
 * Utility functions for Stripe-related operations
 */

/**
 * Enum for Stripe onboarding status
 */
export enum OnboardingStatus {
  Incomplete = 0,
  InProgress = 1,
  Complete = 2,
  Redo = 3
}

/**
 * Enum for Stripe verification status
 */
export enum VerificationStatus {
  NotRequired = 0,
  Pending = 1,
  Required = 2,
  Submitted = 3,
  Verified = 4
}

/**
 * Converts an onboarding status enum value to its string representation
 * @param status - The onboarding status enum value (number)
 * @returns The string representation of the onboarding status
 */
export const getOnboardingStatusString = (status: number | string): string => {
  // If status is already a string, check if it's a valid enum string value
  if (typeof status === 'string') {
    // Check if the string is a valid enum string value
    if (['Incomplete', 'InProgress', 'Complete', 'Redo'].includes(status)) {
      return status;
    }
    
    // Try to parse the string as a number
    const parsedStatus = parseInt(status, 10);
    if (!isNaN(parsedStatus)) {
      status = parsedStatus;
    } else {
      // Default to Incomplete if the string can't be parsed
      return 'Incomplete';
    }
  }
  
  // Convert numeric enum value to string
  switch (status) {
    case OnboardingStatus.Incomplete:
      return 'Incomplete';
    case OnboardingStatus.InProgress:
      return 'InProgress';
    case OnboardingStatus.Complete:
      return 'Complete';
    case OnboardingStatus.Redo:
      return 'Redo';
    default:
      return 'Incomplete'; // Default to Incomplete for unknown values
  }
};

/**
 * Converts a verification status enum value to its string representation
 * @param status - The verification status enum value (number)
 * @returns The string representation of the verification status
 */
export const getVerificationStatusString = (status: number | string): string => {
  // If status is already a string, check if it's a valid enum string value
  if (typeof status === 'string') {
    // Check if the string is a valid enum string value
    if (['NotRequired', 'Pending', 'Required', 'Submitted', 'Verified'].includes(status)) {
      return status;
    }
    
    // Try to parse the string as a number
    const parsedStatus = parseInt(status, 10);
    if (!isNaN(parsedStatus)) {
      status = parsedStatus;
    } else {
      // Default to NotRequired if the string can't be parsed
      return 'NotRequired';
    }
  }
  
  // Convert numeric enum value to string
  switch (status) {
    case VerificationStatus.NotRequired:
      return 'NotRequired';
    case VerificationStatus.Pending:
      return 'Pending';
    case VerificationStatus.Required:
      return 'Required';
    case VerificationStatus.Submitted:
      return 'Submitted';
    case VerificationStatus.Verified:
      return 'Verified';
    default:
      return 'NotRequired'; // Default to NotRequired for unknown values
  }
};